import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { SVGPureIcon } from '@/ui';
import { Tooltip } from 'antd';
import { HelperCore } from '@/common/dependencies';
import copy from 'copy-to-clipboard';
import { markdownToPlainText } from '@shared/utils';
import Other from '@/common/helper/other';
import { atomOneDark, atomOneLight } from '@fengkx/react-syntax-highlighter/dist/esm/styles/hljs';
import globalStore from '@/store/global';
import TextSwitch from '../switch';
import Markdown from '../markdown';
import HtmlViewTemplate from './view-template';
import { request } from '@/common';
import { toPng, toBlob }  from "html-to-image";
import { Dropdown } from 'antd';
import "./index.scss";

const isWeixin = /micromessenger/i.test(navigator.userAgent);

const HtmlViewer = observer((props) => {
  HelperCore.log('>>>HtmlViewer', props);

  const {
    conversation_id,
    msg_id,
    url,
    shareUrl,
    content : rawContent = '',
    title = '代码',
    fileName = `${+new Date}.html`,
    mcpStore,
    onClose,
    supportTheme = false,
    type = 'agent'
  } = props;

  const mapSecurityConfig =`
  <script type="text/javascript">
    window._AMapSecurityConfig = {
      serviceHost: "https://bot.n.cn/_AMapService",
    };
  </script>
  `
  const content = rawContent.replace(/<script src="https:\/\/webapi\.amap\.com/g, mapSecurityConfig + '<script src="https://webapi.amap.com');
  
  const [autoScroll, setAutoScroll] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const refScroll = useRef(null);
  const refIframe = useRef(null);
  const [shareId, setShareId] = useState('');

  useEffect(() => {
    if (/^https\:\/\/(\w{6,})\.n\.cn/.test(shareUrl)) {
      setShareId(RegExp.$1)
    }
  }, [shareUrl]);

  useEffect(() => {
    const el = refIframe?.current;
    if (el) {
      // html预览时，提示加载中
      if (mcpStore.isHtmlPreview && !downloading) {
        setSpinning(true)
        try {
          el?.contentWindow?.addEventListener('DOMContentLoaded', () => {
            setSpinning(false)
          });
        } catch (err) {}
        setTimeout(() => {
          if (mcpStore.isHtmlPreview && !downloading) {
            setSpinning(false);
          }
        }, 2000);
      }
    }
  }, [mcpStore.isHtmlPreview]);

  useEffect(() => {
    setAutoScroll(true);
    const el = refScroll?.current;
    el?.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
    // if (!mcpStore.isHtmlPreview && !mcpStore.isTypingHtml) {
    //   mcpStore.toggleHtmlPreview();
    // }
  }, [fileName]);

  useEffect(() => {
    const el = refScroll?.current;
    if (!(el && autoScroll)) {
      return;
    }

    el.scrollTo({
      left: 0,
      top: mcpStore.isTypingHtml ? el.scrollHeight : 0,
      // behavior: 'smooth',
    });
  }, [content]);

  const copyUrl = () => {
    const copied = copy(shareUrl || url);
    copied && Other.toast('链接已复制', "success");
  }

  const shareHtml = () => {
    request.post('/share/gen', {
        cid: conversation_id,
        msg_id,
        type: 1,
    })
    .then(res => {
      const { id } = res?.data;
      if (id) {
        // const url = `${location.protocol}//${location.host}/share/html?id=${id}`;
        const url = `${location.protocol}//${id}.n.cn`;
        const copied = copy(url);
        copied && Other.toast('分享链接已复制', "success");
      }
    })
    .catch(err => null);
  }

  const copyFile = () => {
    const plainText = markdownToPlainText(content);
    const copied = copy(plainText);
    copied && Other.toast('复制成功', "success");
  }

  const downloadFile = async (fileType) => {
    let _fileName = fileName;
    if (!_fileName.endsWith('.html')) {
      _fileName = fileName + '.html';
    }
    if (type === 'agent') {
      globalStore.hrefNavigate(HelperCore.getQiNiuDownloadUrl(url, _fileName));
    } else {
      if (fileType === 'code') {
        const blob = new Blob([content], {'type': 'text/html'});
        const url = URL.createObjectURL(blob);
        HelperCore.saveFile(url, _fileName)
      } else if (fileType === 'img') {
        const el = refIframe?.current;
        try {
          const blob = await toBlob(el.contentDocument.body)
          const url = URL.createObjectURL(blob);
          HelperCore.saveFile(url, _fileName + '.png')
        } catch (err) {
          //
        }
        setDownloading(false)
        setSpinning(false)
      }
    }
  }

  const closeFile = () => {
    onClose?.()
  }

  const onScroll = (e) => {
    const target = e.target;
    if (target.scrollHeight > target.scrollTop + target.offsetHeight + 60) {
      setAutoScroll(false)
    } else {
      setAutoScroll(true)
    }
  }

  useEffect(() => {
    HelperCore.log("html-viewer render");

    const el = refScroll?.current;
    el?.addEventListener('scroll', onScroll);

    return () => {
      el?.removeEventListener('scroll', onScroll);
    }
  }, []);

  const menus = [
    {
      key: '1',
      label: (
        <div
          className='flex flex-row justify-left items-center'
          onClick={() => {
            setDownloading(true)
            setSpinning(true)
            let time = 300;
            if (!mcpStore.isHtmlPreview) {
              mcpStore.toggleHtmlPreview();
              time = 800;
            }
            setTimeout(() => {
              downloadFile('img');
            }, time);
          }}
        >
          <SVGPureIcon name="chat_share_img" className="size-20px mr-8px" />
          导出为图片
          </div>
      ),
    },
    {
      key: '2',
      label: (
        <div
          className='flex flex-row justify-left items-center'
          onClick={() => {
            let time = 20;
            if (mcpStore.isHtmlPreview) {
              mcpStore.toggleHtmlPreview();
              time = 100;
            }
            setTimeout(() => {
              downloadFile('code');
            }, time);
          }}
        >
          <SVGPureIcon name="code" className="size-20px mr-8px" />
          下载源文件
          </div>
      ),
    },
  ]

  const isDark = mcpStore.htmlTheme == "dark";

  return (
    <section className="html-viewer size-full px-24px py-20px flex flex-col relative z-200 bg-white">
      {
        spinning ? (
          <div
            className="w-100px h-36px border-1px border-black border-opacity-10 rounded-12px bg-white flex justify-left items-center absolute left-[50%] top-112px z-2 ml-[-50px]"
            style={{
              boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.1)',
            }}
          >
            <SVGPureIcon
              className="w-[16px] h-[16px] animate-spin ml-16px mr-8px text-black"
              name="loading"
            ></SVGPureIcon>
            <span className='text-12px'>加载中...</span>
          </div>
        ) : null
      }
      {type === 'agent' ? (
        <header className="h-52px pb-20px border-bottom-1px border-[#e6e6e6] flex justify-left items-center">
          {mcpStore.hasHtmlFinished ? (
            <TextSwitch 
              text1 = "运行"
              text2 = "代码"
              checked={mcpStore.isHtmlPreview}
              onChange={mcpStore.toggleHtmlPreview}
              />
          ) : null}
          <div className="w-0 flex-grow"></div>
          {
            shareUrl
            ? (
              <Tooltip title="分享" destroyTooltipOnHide>
                <div
                  className="mr-12px"
                  onClick={copyUrl}
                >
                  <SVGPureIcon
                    name="icon_share"
                    className="text-black size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
                  />
                </div>
              </Tooltip>
            )
            : null
          }
          <Tooltip title="复制" destroyTooltipOnHide>
            <div
              className="mr-12px"
              onClick={copyFile}
            >
              <SVGPureIcon
                name="icon_copy"
                className="size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
              />
            </div>
          </Tooltip>
          {
            type != "agent" || url
            ? (
              <Tooltip title="下载" destroyTooltipOnHide>
                <div
                  className="mr-12px"
                  onClick={downloadFile}
                >
                  <SVGPureIcon
                    name="icon_down"
                    className="size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
                  />
                </div>
              </Tooltip>
            )
            : null
          }
          {
            /*
            supportTheme
            ? (
              <Tooltip title={isDark ? '切换主题' : '切换主题'} destroyTooltipOnHide>
                <div
                  className="mr-12px"
                  onClick={mcpStore.toggleHtmlTheme}
                >
                  <SVGPureIcon
                    name="dark"
                    className="size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
                  />
                </div>
              </Tooltip>
            )
            : null
            */
          }
          <Tooltip title="关闭" destroyTooltipOnHide>
            <div
              onClick={closeFile}
            >
              <SVGPureIcon
                name="icon_close"
                className="size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
              />
            </div>
          </Tooltip>
        </header>
      ) : (
        <>
        <header className="min-w-450px h-32px mb-16px flex justify-between items-center relative">
          <strong className="text-[16px] truncate max-w-2/5">{title}</strong>
          <div className="absolute left-1/2 ml-[-50px]">
            {mcpStore.hasHtmlFinished ? (
              <TextSwitch 
                text1 = "运行"
                text2 = "代码"
                checked={mcpStore.isHtmlPreview}
                onChange={mcpStore.toggleHtmlPreview}
              />
              ) : null
            }
          </div>
          <div className="flex flex-grow"></div>
          {
            !mcpStore.isTypingHtml ? (
              <>
              <Tooltip title="分享" destroyTooltipOnHide>
                <div
                  className="mr-12px"
                  onClick={shareHtml}
                >
                  <SVGPureIcon
                    name="icon_share"
                    className="text-black size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
                  />
                </div>
              </Tooltip>
              <Dropdown
                menu={{ items:menus }}
                overlayClassName="dropdown"
              >
                <div className="mr-12px">
                  <SVGPureIcon
                    name="icon_down"
                    className="size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
                  />
                </div>
              </Dropdown>
              </>
            ) : null
          }
          <Tooltip title="关闭" destroyTooltipOnHide>
            <div
              onClick={closeFile}
            >
              <SVGPureIcon
                name="icon_close"
                className="size-28px cursor-pointer hover:bg-[#f7f7f7] rounded-6px"
              />
            </div>
          </Tooltip>
        </header>
        <div className="h-32px bg-[#f7f7f7] flex items-center rounded-8px">
          <SVGPureIcon
            name="icon_cmd"
            className="size-18px ml-12px mr-8px"
          />
          <p className="flex-1 mb-0 text-[14px] text-[#7b7b7b]">{fileName}</p>
          <Tooltip title="复制" destroyTooltipOnHide>
            <div
              className="mr-8px"
              onClick={copyFile}
            >
              <SVGPureIcon
                name="icon_copy"
                className="size-28px cursor-pointer hover:bg-[rgba(0,0,0,.03)] rounded-8px"
              />
            </div>
          </Tooltip>
        </div>
        </>
      )
      }
      {type === 'agent' ? (
        <div className="doc flex-1 overflow-y-auto mt-8px" ref={refScroll}>
          {
            !mcpStore.isHtmlPreview
            ? (
              isDark
              ? (
                <Markdown
                  typing={false}
                  text={mcpStore.isTypingHtml ? '```text\n' + content?.trim() + '\n```' : '```html\n' + content?.trim() + '\n```'}
                  hideLanguage
                  highlighterConfig={{
                    className : "highlighter-box highlighter-box-dark",
                    style : atomOneDark,
                    showLineNumbers : true,
                  }}
                  components={{

                  }}
                />
              )
              : (
                <Markdown
                  typing={false}
                  text={mcpStore.isTypingHtml ? '```text\n' + content?.trim() + '\n```' : '```html\n' + content?.trim() + '\n```'}
                  hideLanguage
                  highlighterConfig={{
                    className : "highlighter-box highlighter-box-light",
                    style : atomOneLight,
                    showLineNumbers : true,
                  }}
                  components={{

                  }}
                />
              )
            )
            : (
              isWeixin && shareId ? 
                <iframe 
                  ref={refIframe}
                  src={`/api/html/detail?id=${shareId}`}
                  className='size-full'
                  sandbox='allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox'
                /> : 
                <iframe 
                  ref={refIframe}
                  src='about:srcdoc'
                  className='size-full'
                  srcDoc={HtmlViewTemplate.replace('{html_content_holder}', content)}
                  sandbox='allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox'
                />
            )
          }
        </div>
      ) : (
        <div className="doc flex-1 overflow-y-auto mt-8px" ref={refScroll}>
          {
            !mcpStore.isHtmlPreview
            ? (
              <div className='relative'>
                {
                  mcpStore.isTypingHtml ? <div className='size-full bg-transparent absolute z-2'></div> : null
                }
                <Markdown
                  typing={false}
                  text={'```html\n' + content?.trim() + '\n```'}
                  hideLanguage
                  highlighterConfig={{
                    className : "highlighter-box highlighter-box-light",
                    style : atomOneLight,
                    showLineNumbers : true,
                  }}
                  components={{

                  }}
                />
              </div>
            )
            : (
              <iframe 
                ref={refIframe}
                src='about:srcdoc'
                className='size-full'
                srcDoc={HtmlViewTemplate.replace('{html_content_holder}', content)}
                sandbox='allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox'
                />
            )
          }
        </div>
      )
      }
    </section>
  )
});

export default HtmlViewer;